import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  MenuItem,
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import { useTranslation } from "react-i18next";
import timezonesData from "../../utils/data/timezones.json";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";

interface PatientDialogProps {
  open: boolean;
  newPatientId: string;
  addPatientError: string;
  timezone: string;
  dob: string;
  gender: string;
  onTimezoneChange: (newTimezone: string) => void;
  onClose: () => void;
  onRegister: () => void;
  onInputChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

interface TimezoneOption {
  offset: string; // e.g. "UTC +2"
  label: string; // e.g. "UTC +2 (Oslo, Stockholm, Cairo, Helsinki)"
}

// Helper function to parse a timezone offset string into a number.
const parseUTCOffset = (offset: string): number => {
  const cleaned = offset.replace("UTC", "").replace(/\s/g, "");
  const parts = cleaned.split(":");
  const hours = parseFloat(parts[0]);
  const minutes = parts[1] ? parseFloat(parts[1]) : 0;
  return hours + minutes / 60;
};

// Flatten the JSON data into an array of options.
const flattenTimezones = (): TimezoneOption[] => {
  const options: TimezoneOption[] = [];
  for (const [offset, cities] of Object.entries(timezonesData)) {
    const label = `${offset} (${cities.join(", ")})`;
    options.push({ offset, label });
  }
  options.sort((a, b) => parseUTCOffset(a.offset) - parseUTCOffset(b.offset));
  return options;
};

const timezoneOptions = flattenTimezones();

// Helper function to get the browser's timezone in the format "UTC +X" or "UTC +X:XX"
const getBrowserTimezone = (): string => {
  const timezoneOffset = -new Date().getTimezoneOffset();
  const sign = timezoneOffset >= 0 ? "+" : "-";
  const absolute = Math.abs(timezoneOffset);
  const hours = Math.floor(absolute / 60);
  const minutes = absolute % 60;
  let formatted = `UTC ${sign}${hours}`;
  if (minutes > 0) {
    formatted += `:${minutes.toString().padStart(2, "0")}`;
  }
  return formatted;
};

// Helper function to validate and parse the date string.
const getValidDate = (dob: string): Date | null => {
  if (!dob) return null;
  const parsedDate = new Date(dob);
  return isNaN(parsedDate.getTime()) ? null : parsedDate;
};

const PatientDialog: React.FC<PatientDialogProps> = ({
  open,
  newPatientId,
  addPatientError,
  timezone,
  dob,
  gender,
  onTimezoneChange,
  onClose,
  onRegister,
  onInputChange,
}) => {
  const { t } = useTranslation();

  // Use the provided timezone if available; otherwise, fall back to the browser's timezone.
  const effectiveTimezone = timezone || getBrowserTimezone();

  useEffect(() => {
    if (!timezone) {
      onTimezoneChange(getBrowserTimezone());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const effectiveOffset = parseUTCOffset(effectiveTimezone);

  let currentOption =
    timezoneOptions.find(
      (option) =>
        Math.abs(parseUTCOffset(option.offset) - effectiveOffset) < 0.001
    ) || null;

  if (!currentOption) {
    const browserTz = getBrowserTimezone();
    const browserOffset = parseUTCOffset(browserTz);
    currentOption =
      timezoneOptions.find(
        (option) =>
          Math.abs(parseUTCOffset(option.offset) - browserOffset) < 0.001
      ) || null;
  }

  //console.log("Effective timezone:", effectiveTimezone);
  //console.log("Effective offset:", effectiveOffset);
  //console.log("Timezone options offsets:", timezoneOptions.map((o) => o.offset));
  //console.log("Current timezone option:", currentOption);

  // Local state for the date value used by the DatePicker.
  const [selectedDate, setSelectedDate] = useState<Date | null>(
    getValidDate(dob)
  );

  // Update local state if the incoming dob prop changes.
  useEffect(() => {
    setSelectedDate(getValidDate(dob));
  }, [dob]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{t("Enroll New Patient")}</DialogTitle>
      <DialogContent sx={{ pb: 2 }}>
        <TextField
          margin="normal"
          label={t("Patient ID")}
          name="newPatientId"
          value={newPatientId}
          onChange={onInputChange}
          fullWidth
        />
        <TextField
          margin="normal"
          label={t("First Name")}
          name="firstName"
          onChange={onInputChange}
          fullWidth
        />
        <TextField
          margin="normal"
          label={t("Last Name")}
          name="lastName"
          onChange={onInputChange}
          fullWidth
        />
        <TextField
          margin="normal"
          label={t("phone")}
          name="phone"
          onChange={onInputChange}
          fullWidth
        />
        <TextField
          margin="normal"
          label={t("Email ID")}
          name="emailId"
          onChange={onInputChange}
          fullWidth
        />
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            label={t("date_of_birth")}
            value={selectedDate}
            inputFormat="MM/dd/yyyy"
            mask="__/__/____"
            maxDate={new Date()}
            onError={(error, value) => {
              console.error("DatePicker error:", error);
            }}
            onChange={(newValue) => {
              setSelectedDate(newValue);
              if (newValue && !isNaN(newValue.getTime())) {
                const formattedDate = newValue.toISOString().split("T")[0];
                onInputChange({
                  target: { name: "dob", value: formattedDate },
                } as React.ChangeEvent<HTMLInputElement>);
              } else {
                onInputChange({
                  target: { name: "dob", value: "" },
                } as React.ChangeEvent<HTMLInputElement>);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                margin="normal"
                fullWidth
                // Pass readOnly and disable autocomplete in the native input props.
                inputProps={{
                  ...params.inputProps,
                  readOnly: true,
                  autoComplete: "off",
                }}
              />
            )}
          />
        </LocalizationProvider>
        <TextField
          margin="normal"
          label={t("Gender")}
          name="gender"
          select
          value={gender}
          onChange={onInputChange}
          fullWidth
        >
          <MenuItem value="M">M</MenuItem>
          <MenuItem value="F">F</MenuItem>
        </TextField>
        <Autocomplete
          options={timezoneOptions}
          getOptionLabel={(option) => option.label}
          groupBy={(option) => option.offset.split(" ")[0]}
          value={currentOption}
          onChange={(event, newValue) => {
            if (newValue) {
              onTimezoneChange(newValue.offset);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              margin="normal"
              label={t("Timezone")}
              fullWidth
            />
          )}
        />
        {addPatientError && (
          <Typography color="error" sx={{ mt: 2 }}>
            {addPatientError}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t("cancel")}</Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onRegister}
          disabled={!newPatientId}
        >
          {t("Register")}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PatientDialog;
